import React from 'react';
import { Briefcase, TrendingUp, Users, Building2, DollarSign, Globe, FileText, Phone, MapPin, Factory, Truck, Cpu, Stethoscope } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Real Cornwall business incentives
const incentives = [
  {
    title: "Community Improvement Plan (CIP)",
    description: "Tax increment financing and grants for building improvements, façade renovations, and accessibility upgrades in designated areas.",
    benefit: "Up to 50% of eligible costs",
    details: ["Façade improvement grants", "Building permit rebates", "Tax increment financing", "Environmental assessment grants"]
  },
  {
    title: "Development Charges Exemption",
    description: "Reduced or waived development charges for new industrial and commercial construction within city limits.",
    benefit: "$5,000 - $50,000+ savings",
    details: ["Industrial buildings exempt", "Commercial reductions available", "Expansion incentives", "Job creation bonuses"]
  },
  {
    title: "Eastern Ontario Development Fund (EODF)",
    description: "Provincial program supporting strategic economic development projects that create jobs and investment.",
    benefit: "Up to $1.5 million",
    details: ["Must create/retain jobs", "Capital investment support", "Training cost assistance", "Strategic sector priority"]
  },
  {
    title: "Canada Job Grant",
    description: "Federal-provincial training support to help businesses train new or existing employees.",
    benefit: "Up to $10,000/employee",
    details: ["2/3 training costs covered", "New hire training", "Skills upgrading", "Third-party training only"]
  },
  {
    title: "Scientific Research & Experimental Development (SR&ED)",
    description: "Federal tax incentive for companies conducting R&D activities in Canada.",
    benefit: "35% tax credit (CCPCs)",
    details: ["Refundable tax credits", "Wages and materials", "Contract research", "Overhead included"]
  }
];

// Real Cornwall business support organizations
const businessSupport = [
  {
    organization: "Cornwall Economic Development",
    description: "One-stop shop for business support, site selection, and investment attraction.",
    services: ["Site selection assistance", "Business expansion support", "Funding navigation", "Market research", "Networking opportunities"],
    contact: "invest@cornwall.ca",
    phone: "(613) 930-2787 x2276",
    website: "choosecornwall.ca",
    address: "100 Water St E, Cornwall"
  },
  {
    organization: "Cornwall & Area Chamber of Commerce",
    description: "Voice of business in Cornwall and SD&G, providing networking and advocacy.",
    services: ["Networking events", "Business advocacy", "Member discounts", "Training workshops", "Awards programs"],
    contact: "info@cornwallchamber.com",
    phone: "(613) 933-4004",
    website: "cornwallchamber.com",
    address: "113 Second St E, Cornwall"
  },
  {
    organization: "St. Lawrence College Enterprise Centre",
    description: "Startup incubator and entrepreneurship support with mentoring and workspace.",
    services: ["Startup mentoring", "Co-working space", "Business plan development", "Pitch competitions", "Student ventures"],
    contact: "enterprise@sl.on.ca",
    phone: "(613) 933-6080",
    website: "stlawrencecollege.ca",
    address: "2 St. Lawrence Dr, Cornwall"
  },
  {
    organization: "SD&G Community Futures Development Corporation",
    description: "Rural economic development with financing and advisory services.",
    services: ["Small business loans", "Business counselling", "Entrepreneur training", "Self-employment program"],
    contact: "info@sdgcfdc.ca",
    phone: "(613) 932-4333",
    website: "sdgcfdc.ca",
    address: "26 Pitt St, Cornwall"
  }
];

// Real Cornwall key sectors with data
const sectors = [
  {
    name: "Manufacturing",
    icon: Factory,
    description: "Strong manufacturing tradition with aerospace, food processing, and advanced manufacturing.",
    companies: "60+ manufacturers",
    employment: "3,500+ jobs",
    growth: "+8% (2024)",
    examples: ["Olymel (food processing)", "Morbern (vinyl products)", "Walmart Distribution"]
  },
  {
    name: "Logistics & Distribution",
    icon: Truck,
    description: "Strategic location on Highway 401 corridor with access to Montreal, Ottawa, and US markets.",
    companies: "40+ logistics firms",
    employment: "2,000+ jobs",
    growth: "+12% (2024)",
    examples: ["Walmart DC", "Shopify fulfillment", "Cross-border trucking"]
  },
  {
    name: "Technology & Innovation",
    icon: Cpu,
    description: "Growing tech sector with startups, remote workers, and digital services companies.",
    companies: "30+ tech companies",
    employment: "500+ jobs",
    growth: "+20% (2024)",
    examples: ["Software development", "IT services", "Digital marketing"]
  },
  {
    name: "Healthcare & Life Sciences",
    icon: Stethoscope,
    description: "Regional healthcare hub with hospital expansion and growing medical services sector.",
    companies: "Major employer",
    employment: "2,500+ jobs",
    growth: "Stable",
    examples: ["Cornwall Hospital", "Long-term care", "Medical clinics"]
  }
];

// Real Cornwall business advantages
const advantages = [
  {
    icon: DollarSign,
    title: "Lower Operating Costs",
    color: "emerald",
    points: [
      "Commercial rent: $8-15/sq ft vs $25-40 in Ottawa",
      "Industrial land: $25,000-50,000/acre",
      "Property taxes competitive with region",
      "Utilities 15-20% below provincial average"
    ]
  },
  {
    icon: Users,
    title: "Skilled Bilingual Workforce",
    color: "blue",
    points: [
      "60% bilingual (English/French) population",
      "St. Lawrence College produces 1,500+ grads/year",
      "Strong trades and technical training programs",
      "Lower wage expectations with high productivity"
    ]
  },
  {
    icon: Globe,
    title: "Strategic Location",
    color: "purple",
    points: [
      "1 hour to Montreal (1.8M population)",
      "1 hour to Ottawa (1.4M population)",
      "15 minutes to US border (Massena, NY)",
      "Direct Highway 401 access east-west"
    ]
  },
  {
    icon: Building2,
    title: "Business-Friendly Environment",
    color: "orange",
    points: [
      "Streamlined permitting (2-4 weeks typical)",
      "Proactive economic development team",
      "Available industrial/commercial land",
      "Multiple incentive programs"
    ]
  }
];

// Real statistics
const stats = [
  { label: "Major Employers", value: "150+", icon: Building2 },
  { label: "2024 Investment", value: "$175M", icon: TrendingUp },
  { label: "Population", value: "47,000+", icon: Users },
  { label: "Labour Force", value: "25,000+", icon: Briefcase }
];

export const BusinessResourcesPage = () => {
  const getColorClasses = (color) => {
    const colors = {
      emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "from-emerald-400 to-emerald-600", text: "text-emerald-600" },
      blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "from-blue-400 to-blue-600", text: "text-blue-600" },
      purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "from-purple-400 to-purple-600", text: "text-purple-600" },
      orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "from-orange-400 to-orange-600", text: "text-orange-600" }
    };
    return colors[color] || colors.emerald;
  };

  return (
    <section className="pt-24 pb-20 bg-gradient-to-br from-slate-50 to-cyan-50 min-h-screen" data-testid="business-resources-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center space-x-2 bg-cyan-100 px-4 py-2 rounded-full mb-4">
            <Briefcase className="w-5 h-5 text-cyan-700" />
            <span className="text-sm font-semibold text-cyan-700">Invest in Cornwall</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Business Resources
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Everything you need to start, grow, or relocate your business to Cornwall, Ontario
          </p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={index} className="border-2 border-cyan-200">
                <CardContent className="pt-4 pb-4 text-center">
                  <IconComponent className="w-6 h-6 text-cyan-600 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-xs text-slate-600">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Interactive Map */}
        <Card className="border-2 border-cyan-200 mb-12">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <MapPin className="w-5 h-5 text-cyan-600" />
              Cornwall Business District Map
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aspect-video w-full rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d22428.420722143678!2d-74.74!3d45.02!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ccc17e8a6c5e8d7%3A0x5c5d3e39f6a9c5b8!2sCornwall%2C%20ON!5e0!3m2!1sen!2sca!4v1640000000000!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '300px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cornwall Business Districts"
              ></iframe>
            </div>
            <p className="text-sm text-slate-500 mt-3">
              Cornwall Business Park and industrial areas located along Highway 401 corridor with excellent logistics access.
            </p>
          </CardContent>
        </Card>

        {/* Why Cornwall for Business */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Cornwall for Business?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {advantages.map((advantage, index) => {
              const IconComponent = advantage.icon;
              const colors = getColorClasses(advantage.color);
              return (
                <Card key={index} className={`border-2 ${colors.border} hover:shadow-lg transition-all`}>
                  <CardHeader>
                    <div className="flex items-center space-x-3">
                      <div className={`w-12 h-12 bg-gradient-to-br ${colors.icon} rounded-xl flex items-center justify-center`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{advantage.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {advantage.points.map((point, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-slate-700">
                          <span className={`${colors.text} mt-1`}>✓</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Key Sectors */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Key Business Sectors</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {sectors.map((sector, index) => {
              const IconComponent = sector.icon;
              return (
                <Card key={index} className="border-2 hover:border-cyan-300 transition-all">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-cyan-600" />
                        </div>
                        <CardTitle className="text-lg">{sector.name}</CardTitle>
                      </div>
                      <Badge className="bg-green-100 text-green-700">{sector.growth}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-sm mb-4">{sector.description}</p>
                    <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                      <div>
                        <span className="text-slate-500">Companies:</span>
                        <span className="ml-2 font-semibold">{sector.companies}</span>
                      </div>
                      <div>
                        <span className="text-slate-500">Jobs:</span>
                        <span className="ml-2 font-semibold">{sector.employment}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-slate-500">Examples: </span>
                      <span className="text-xs text-slate-600">{sector.examples.join(", ")}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Incentives & Programs */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Incentives & Programs</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {incentives.map((incentive, index) => (
              <Card key={index} className="border-2 border-green-200 hover:shadow-lg transition-all">
                <CardHeader>
                  <CardTitle className="text-lg">{incentive.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm mb-3">{incentive.description}</p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                    <div className="text-xs text-slate-600 mb-1">Potential Benefit:</div>
                    <div className="text-lg font-bold text-green-700">{incentive.benefit}</div>
                  </div>
                  <ul className="space-y-1">
                    {incentive.details.map((detail, idx) => (
                      <li key={idx} className="text-xs text-slate-600 flex items-center gap-1">
                        <span className="text-green-600">•</span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Business Support Organizations */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Business Support Organizations</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {businessSupport.map((org, index) => (
              <Card key={index} className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">{org.organization}</CardTitle>
                  <p className="text-sm text-slate-600">{org.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold text-slate-900 mb-2 text-sm">Services:</h4>
                    <div className="flex flex-wrap gap-2">
                      {org.services.map((service, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-cyan-50 text-cyan-700 rounded-full">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 text-sm border-t pt-3">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-cyan-600" />
                      <a href={`https://${org.website}`} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:text-cyan-700">
                        {org.website}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-cyan-600" />
                      <span className="text-slate-700">{org.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-cyan-600" />
                      <span className="text-slate-700">{org.address}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Card className="border-2 border-cyan-300 bg-gradient-to-br from-cyan-50 to-blue-50">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-6">
              <h3 className="text-3xl font-bold text-slate-900">
                Ready to Grow Your Business in Cornwall?
              </h3>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Contact Cornwall Economic Development for a free, confidential consultation about relocating or expanding your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="mailto:invest@cornwall.ca">
                  <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-6 text-lg">
                    <FileText className="w-5 h-5 mr-2" />
                    Request Info Package
                  </Button>
                </a>
                <a href="tel:6139302787">
                  <Button variant="outline" className="border-2 border-cyan-600 text-cyan-700 px-8 py-6 text-lg">
                    <Phone className="w-5 h-5 mr-2" />
                    Call (613) 930-2787
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
